import type { Intersection } from "three";

import {
  POINTER_GESTURE_MOVE,
  POINTER_GESTURE_PAN_BEGIN,
  POINTER_GESTURE_PAN_END,
  POINTER_GESTURE_PINCH_BEGIN,
  POINTER_GESTURE_PINCH_END,
  POINTER_GESTURE_PITCH_BEGIN,
  POINTER_GESTURE_PITCH_END,
  POINTER_GESTURE_PRESS,
  POINTER_GESTURE_RELEASE,
  type PointerGestureAxisSessionDetail,
  type PointerGestureMoveDetail,
  type PointerGesturePinchDetail,
  type PointerGesturePressDetail,
  type PointerGestureReleaseDetail,
} from "../../input/PointerGestureInterpreter";
import type { SugarModel } from "../../loader/SugarModel";
import type { Room } from "../../shape/Scene";

/**
 * Structural stand-in for the room designer instance carried by
 * `designer-scene-ready`. Core must not depend on a product's concrete
 * interface (`products/room-designer/types/IRoomDesigner`) — consumers inside
 * the product narrow it at the listener edge:
 *
 *   (ev as CustomEvent<{ designer: DesignerSceneHost }>).detail.designer
 */
export interface DesignerSceneHost {
  readonly scene: unknown;
}

export const DESIGNER_SELECT_PRODUCT_COMMAND = "select-product";
export const DESIGNER_SELECT_WALL_COMMAND = "select-wall";
export const DESIGNER_CLEAR_SELECTION_COMMAND = "unselect";
export const DESIGNER_SELECTABLE_ON_COMMAND = "on-selectable";
export const DESIGNER_SELECTABLE_OFF_COMMAND = "off-selectable";
export const DESIGNER_SELECTION_CHANGED_EVENT = "designer-selection-changed";

export const DESIGNER_REQUEST_RENDER_COMMAND = "request-update";
export const DESIGNER_DRAG_STARTED_EVENT = "dragging-start";
export const DESIGNER_DRAG_ENDED_EVENT = "dragging-end";
export const DESIGNER_PRODUCT_TRANSFORMED_EVENT =
  "product-transform-changed";
export const DESIGNER_PRODUCT_DELETED_EVENT = "delete-product";

export const DESIGNER_GET_RENDER_MODE_COMMAND = "get-render-mode";
export const DESIGNER_ENABLE_DRAW_COMMAND = "enable-draw";
export const DESIGNER_SCENE_READY_EVENT = "designer-scene-ready";
export const DESIGNER_SCENE_CHANGED_EVENT = "scene-changed";
export const DESIGNER_SCENE_LOADED_EVENT = "on-scene-loaded";
export const DESIGNER_DRAWER_STATE_EVENT = "drawer-state";
export const DESIGNER_RENDER_MODE_CHANGED_EVENT = "render-mode-changed";

export type DesignerSelectProductDetail = {
  model: SugarModel;
  sourceModel?: SugarModel;
  part?: unknown;
  intersections?: Intersection[];
  event?: Event;
};

export type DesignerSelectWallDetail = {
  wall: unknown;
  intersections?: Intersection[];
  event?: Event;
  ref?: unknown;
};

export type DesignerSelectionChangedDetail =
  | { kind: "product"; target: SugarModel }
  | { kind: "wall"; target: unknown }
  | { kind: "none"; target?: undefined };

export interface DesignerInputEventMap {
  [POINTER_GESTURE_PRESS]: PointerGesturePressDetail;
  [POINTER_GESTURE_RELEASE]: PointerGestureReleaseDetail;
  [POINTER_GESTURE_MOVE]: PointerGestureMoveDetail;
  [POINTER_GESTURE_PAN_BEGIN]: PointerGestureAxisSessionDetail;
  [POINTER_GESTURE_PAN_END]: PointerGestureAxisSessionDetail;
  [POINTER_GESTURE_PITCH_BEGIN]: PointerGestureAxisSessionDetail;
  [POINTER_GESTURE_PITCH_END]: PointerGestureAxisSessionDetail;
  [POINTER_GESTURE_PINCH_BEGIN]: PointerGesturePinchDetail;
  [POINTER_GESTURE_PINCH_END]: PointerGesturePinchDetail;
}

export interface DesignerSelectionCommandMap {
  [DESIGNER_SELECT_PRODUCT_COMMAND]: DesignerSelectProductDetail;
  [DESIGNER_SELECT_WALL_COMMAND]: DesignerSelectWallDetail;
  [DESIGNER_CLEAR_SELECTION_COMMAND]: undefined;
  [DESIGNER_SELECTABLE_ON_COMMAND]: undefined;
  [DESIGNER_SELECTABLE_OFF_COMMAND]: undefined;
}

export interface DesignerSelectionEventMap {
  [DESIGNER_SELECTION_CHANGED_EVENT]: DesignerSelectionChangedDetail;
}

export interface DesignerTransformCommandMap {
  [DESIGNER_REQUEST_RENDER_COMMAND]: undefined;
}

export interface DesignerTransformEventMap {
  [DESIGNER_DRAG_STARTED_EVENT]: undefined;
  [DESIGNER_DRAG_ENDED_EVENT]: undefined;
  [DESIGNER_PRODUCT_TRANSFORMED_EVENT]: { model: SugarModel };
  [DESIGNER_PRODUCT_DELETED_EVENT]: { model: SugarModel };
}

export interface DesignerConfigurationCommandMap {
  [DESIGNER_GET_RENDER_MODE_COMMAND]: undefined;
  [DESIGNER_ENABLE_DRAW_COMMAND]: boolean;
}

export interface DesignerConfigurationEventMap {
  [DESIGNER_SCENE_READY_EVENT]: { designer: DesignerSceneHost };
  [DESIGNER_SCENE_CHANGED_EVENT]: Room;
  [DESIGNER_SCENE_LOADED_EVENT]: Room | undefined;
  [DESIGNER_DRAWER_STATE_EVENT]: string;
  [DESIGNER_RENDER_MODE_CHANGED_EVENT]: string;
}
